import { CSSProperties, FormEventHandler, memo, ReactNode, useCallback } from 'react'
import cn from 'classnames'
import { IconSpinner } from 'components/IconSpinner'
import { KeyCodeEnum } from 'enums/keyCodes'
import { useHotKey } from 'hooks/useHotKey'
import { stopBubbling } from 'utils/event'

import { DialogMobile } from '../DialogMobile'

import s from './ModalMobile.module.less'

interface IProps {
  children: ReactNode
  action?: ReactNode
  className?: string
  /** Слот для нижнего блока мобильного диалога */
  footer?: ReactNode
  footerClassName?: string
  formName?: string
  /** Слот для верхнего левого блока мобильного диалога */
  header?: ReactNode
  /** Слот для верхнего правого блока мобильного диалога */
  headerAdditional?: ReactNode
  id?: string
  /** Закрытие окна по нажатию на Esc */
  isCloseOnEsc?: boolean
  /** Закрытие окна по тапу вне области этого окна */
  isCloseOnOutsideClick?: boolean
  /** Отображение окна как диалогового (прижатого к низу) */
  isDialogType?: boolean
  /** Отображение загрузки данных окна */
  isLoading?: boolean
  /** Отображение горизонтальных и вертикальных отступов контентной части */
  isOffsets?: boolean
  /** Отображение кнопки закрытия окна */
  isShowCloseButton?: boolean
  onClose?: () => void
  onSubmit?: FormEventHandler<HTMLFormElement>
  style?: CSSProperties
}

export const ModalMobile = memo<IProps>(function ModalMobile({
  action,
  children,
  className,
  footerClassName,
  footer,
  formName,
  header,
  headerAdditional,
  id,
  isCloseOnEsc = true,
  isCloseOnOutsideClick = true,
  isShowCloseButton = true,
  isDialogType = false,
  isLoading = false,
  isOffsets = true,
  onClose,
  onSubmit,
  style
}) {
  const handlePressOnEsc = useCallback(() => {
    if (isCloseOnEsc && onClose) {
      onClose()
    }
  }, [isCloseOnEsc, onClose])

  useHotKey(KeyCodeEnum.ESC, handlePressOnEsc)

  const content = () => {
    if (isLoading) {
      return <IconSpinner size="nm" />
    }

    const type = isDialogType ? 'dialog' : 'common'

    return (
      <DialogMobile
        action={action}
        className={cn(s.content, s[type])}
        footer={footer}
        footerClassName={footerClassName}
        header={header}
        headerAdditional={headerAdditional}
        id={id}
        isCloseOnOutsideClick={isCloseOnOutsideClick}
        isDialogType={isDialogType}
        isOffsets={isOffsets}
        isShowCloseButton={isShowCloseButton}
        onClose={onClose}
      >
        {children}
      </DialogMobile>
    )
  }

  const isForm = !!onSubmit
  const props = {
    className: cn(s.ModalMobile, className, {
      [s.ModalMobile_dialogType]: isDialogType,
      [s.ModalMobile_loading]: isLoading
    }),
    id,
    onClick: stopBubbling,
    style
  }

  return isForm ? (
    <form {...props} name={formName} onSubmit={onSubmit}>
      {content()}
    </form>
  ) : (
    <div {...props}>{content()}</div>
  )
})
